import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Icon, type IconName } from './Icon';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon, drawn at the size's optical weight. */
  icon?: IconName;
  /** Swaps the label for a spinner and disables the button. */
  busy?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  busy = false,
  fullWidth = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        styles.base,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        busy && styles.busy,
        className,
      )}
      {...rest}
    >
      <span className={clsx(styles.label, busy && styles.hidden)}>
        {icon ? <Icon name={icon} size={size === 'lg' ? 18 : 16} /> : null}
        {children}
      </span>
      {busy ? (
        <span className={styles.busyOverlay}>
          <span className={styles.spinner} />
        </span>
      ) : null}
    </button>
  );
}
